/**
 * Formats a phone number string to (xxx) xxx-xxxx format
 * @param {string} digits - Phone number digits (can contain non-digits which will be removed)
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(digits) {
  if (!digits) return '';

  // Remove all non-digit characters
  digits = digits.replace(/\D/g, '');

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
  return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
}

/**
 * Initializes phone number formatting for a given input element
 * @param {HTMLInputElement} phoneInput - The input element to format
 */
export function initializePhoneFormatting(phoneInput) {
  if (!phoneInput) return;

  // Format existing value if present
  if (phoneInput.value) {
    phoneInput.value = formatPhoneNumber(phoneInput.value);
  }

  phoneInput.addEventListener('input', function (e) {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const oldValue = input.value;

    // Remove all non-digit characters
    let digits = input.value.replace(/\D/g, '');

    // Limit to 10 digits
    if (digits.length > 10) {
      digits = digits.substring(0, 10);
    }

    // Format the phone number
    let formatted = formatPhoneNumber(digits);

    // Calculate new cursor position based on digits before cursor in old value
    const digitsBeforeCursor = oldValue.substring(0, cursorPosition).replace(/\D/g, '').length;
    let newCursorPosition = 0;

    if (digitsBeforeCursor <= 0) {
      newCursorPosition = 1; // After opening parenthesis
    } else if (digitsBeforeCursor <= 3) {
      newCursorPosition = digitsBeforeCursor + 1; // Inside area code
    } else if (digitsBeforeCursor <= 6) {
      newCursorPosition = digitsBeforeCursor + 3; // After area code
    } else {
      newCursorPosition = digitsBeforeCursor + 4; // After first part
    }

    // Ensure cursor doesn't exceed formatted length
    if (newCursorPosition > formatted.length) {
      newCursorPosition = formatted.length;
    }

    // Update the input value
    input.value = formatted;

    // Set cursor position
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  });

  // Handle paste event
  phoneInput.addEventListener('paste', function (e) {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const digits = pastedText.replace(/\D/g, '').substring(0, 10);
    phoneInput.value = formatPhoneNumber(digits);
  });
}

/**
 * Initializes phone number formatting for all phone inputs on the page
 * Can be called with a selector to target specific inputs
 * @param {string} selector - CSS selector for phone inputs (default: 'input[type="tel"]')
 */
export function initializePhoneFormattingForPage(selector = 'input[type="tel"]') {
  document.addEventListener('DOMContentLoaded', function () {
    const phoneInputs = document.querySelectorAll(selector);
    phoneInputs.forEach((input) => {
      initializePhoneFormatting(input);
    });
  });
}

/**
 * Setup function for phone formatting - initializes all phone inputs on the page
 * This is the main entry point for use in main.js
 */
export default function setupPhoneFormatting() {
  // Initialize existing phone inputs on page load
  const phoneInputs = document.querySelectorAll('input[type="tel"]');
  phoneInputs.forEach((input) => {
    initializePhoneFormatting(input);
  });

  // Listen for HTMX swaps to initialize phone formatting for dynamically loaded content
  document.addEventListener('htmx:afterSwap', function(event) {
    // Find all phone inputs in the swapped content
    const swappedElement = event.detail.target;
    if (swappedElement) {
      const phoneInputs = swappedElement.querySelectorAll('input[type="tel"]');
      phoneInputs.forEach((input) => {
        // Check if already initialized to avoid duplicate handlers
        if (!input.dataset.phoneFormattingInitialized) {
          initializePhoneFormatting(input);
          input.dataset.phoneFormattingInitialized = 'true';
        }
      });
    }
  });

  // Also listen for AJAX content loads (for non-HTMX dynamic content)
  // This covers cases where content is loaded via jQuery AJAX
  let initializationTimeout;
  const observer = new MutationObserver(function(mutations) {
    // Debounce initialization to batch multiple DOM changes
    clearTimeout(initializationTimeout);
    initializationTimeout = setTimeout(function() {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            const phoneInputs = node.querySelectorAll ? node.querySelectorAll('input[type="tel"]') : [];
            phoneInputs.forEach((input) => {
              if (!input.dataset.phoneFormattingInitialized) {
                initializePhoneFormatting(input);
                input.dataset.phoneFormattingInitialized = 'true';
              }
            });
            // Also check if the node itself is a phone input
            if (node.tagName === 'INPUT' && node.type === 'tel' && !node.dataset.phoneFormattingInitialized) {
              initializePhoneFormatting(node);
              node.dataset.phoneFormattingInitialized = 'true';
            }
          }
        });
      });
    }, 100); // Small delay to batch DOM changes
  });

  // Start observing the document body for added nodes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Also expose a global function for manual initialization if needed
  window.reinitializePhoneFormatting = function(container) {
    const phoneInputs = (container || document).querySelectorAll('input[type="tel"]');
    phoneInputs.forEach((input) => {
      if (!input.dataset.phoneFormattingInitialized) {
        initializePhoneFormatting(input);
        input.dataset.phoneFormattingInitialized = 'true';
      }
    });
  };
}

